/**
 * Workflow Context Store
 * Persists offer, audience, and product details across all monetization steps.
 * Uses Zustand with localStorage persistence.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const initialState = {
  // Offer details
  offerName: '',
  offerDescription: '',
  offerTagline: '',
  offerType: '',
  audience: '',
  audiencePainPoint: '',
  price: '',
  benefits: [],
  bonuses: [],
  urgency: '',
  
  // Workflow completion flags
  offerCreated: false,
  salesPageCreated: false,
  emailCampaignCreated: false,
  contentCreated: false,
  
  // Timestamps
  offerCreatedAt: null,
  salesPageCreatedAt: null,
  emailCampaignCreatedAt: null,
  
  // IDs for linking
  offerId: null,
  salesPageId: null,
  emailCampaignId: null
};

export const useWorkflowStore = create(
  persist(
    (set, get) => ({
      ...initialState,
      
      // Set offer data after Build Offer completion
      setOfferData: (data) => set({
        offerName: data.offerName || data.offer_name || '',
        offerDescription: data.offerDescription || data.offer_description || '',
        offerTagline: data.offerTagline || data.offer_tagline || '',
        offerType: data.offerType || data.offer_type || '',
        audience: data.audience || data.target_audience || '',
        audiencePainPoint: data.audiencePainPoint || data.audience_pain_point || '',
        price: data.price || data.price_suggestion || '',
        benefits: data.benefits || data.key_benefits || [],
        bonuses: data.bonuses || [],
        urgency: data.urgency || data.urgency_element || '',
        offerCreated: true,
        offerCreatedAt: new Date().toISOString(),
        offerId: data.offerId || data.id || null
      }),
      
      // Mark sales page as created
      setSalesPageData: (data = {}) => set({
        salesPageCreated: true,
        salesPageCreatedAt: new Date().toISOString(),
        salesPageId: data.salesPageId || data.id || null
      }),
      
      // Mark email campaign as created
      setEmailCampaignData: (data = {}) => set({
        emailCampaignCreated: true,
        emailCampaignCreatedAt: new Date().toISOString(),
        emailCampaignId: data.emailCampaignId || data.id || null
      }),
      
      // Mark content as created
      setContentCreated: () => set({
        contentCreated: true
      }),
      
      // Update specific fields
      updateOfferField: (field, value) => set({ [field]: value }),
      
      // Check if workflow has data
      hasOfferData: () => {
        const state = get();
        return !!(state.offerName || state.offerDescription || state.audience);
      },
      
      // Get offer data for prefilling
      getOfferDataForSalesPage: () => {
        const state = get();
        return {
          offer_name: state.offerName,
          offer_description: state.offerDescription,
          target_audience: state.audience,
          key_benefits: state.benefits,
          price: state.price,
          bonuses: state.bonuses
        };
      },
      
      // Get offer data for email campaign
      getOfferDataForEmailCampaign: () => {
        const state = get();
        return {
          offer_name: state.offerName,
          offer_description: state.offerDescription,
          target_audience: state.audience
        };
      },
      
      // Get workflow status
      getWorkflowStatus: () => {
        const state = get();
        return {
          hasOffer: state.offerCreated,
          hasSalesPage: state.salesPageCreated,
          hasEmailCampaign: state.emailCampaignCreated,
          hasContent: state.contentCreated,
          offerName: state.offerName
        };
      },
      
      // Reset entire workflow (start fresh)
      resetWorkflow: () => set(initialState),
      
      // Reset just the completion flags (keep data for re-use)
      resetCompletionFlags: () => set({
        salesPageCreated: false,
        emailCampaignCreated: false,
        contentCreated: false,
        salesPageCreatedAt: null,
        emailCampaignCreatedAt: null,
        salesPageId: null,
        emailCampaignId: null
      })
    }),
    {
      name: 'paygbot-workflow-store',
      version: 1,
      // Only persist essential data
      partialize: (state) => ({
        offerName: state.offerName,
        offerDescription: state.offerDescription,
        offerTagline: state.offerTagline,
        offerType: state.offerType,
        audience: state.audience,
        audiencePainPoint: state.audiencePainPoint,
        price: state.price,
        benefits: state.benefits,
        bonuses: state.bonuses,
        urgency: state.urgency,
        offerCreated: state.offerCreated,
        salesPageCreated: state.salesPageCreated,
        emailCampaignCreated: state.emailCampaignCreated,
        contentCreated: state.contentCreated,
        offerCreatedAt: state.offerCreatedAt,
        offerId: state.offerId
      })
    }
  )
);

export default useWorkflowStore;
